import React, { memo } from 'react';
import styled from 'styled-components';

import { validateAddress } from '@wecasa/common/src/forms/validators/address';
import { getCurrentCountry } from '@wecasa/common/src/utils/country';

import type { CommonCleaningBasketProps } from '@wecasa/customer/src/components/basket/types';
import { CONFIG_BY_COUNTRY } from '@wecasa/customer/src/constants/country-config';
import useGetPrices from '@wecasa/customer/src/hooks/use-get-prices';
import { useIsITCSelectedOrSuccessfullyPaired } from '@wecasa/customer/src/hooks/use-is-itc-selected-or-successfully-paired';

import Separator from '@wecasa/web-ui/src/components/separator/separator';

import { StyledBasketContainer } from '../common/common';
import NoContentYet from '../common/no-content-yet';
import Address from '../common/sections/address/address';
import ProSection from '../common/sections/pros/pro-section';
import MultipleCleaningAppointments from './sections/appointments/multiple/multiple-cleaning-appointments';
import SingleCleaningAppointments from './sections/appointments/single/single-cleaning-appointments';
import MembershipCard from './sections/membership-card/membership-card';
import MultiplePrestationsDetails from './sections/prestations/multiple/multiple-prestations-details';
import SinglePrestationsDetails from './sections/prestations/single/single-prestations-details';
import TaxCreditInfoAlert from './sections/tax-credit-info/tax-credit-info-alert';

export const BASKET_CLEANING_PRICES_LOADED_TEST_ID = 'basket-cleaning-prices-loaded-test-id';

const MembershipCardSeparator = styled(Separator)`
  margin-bottom: 20px;
`;

export type CleaningBasketProps = CommonCleaningBasketProps & {
  pathname: string;
  setIsMembershipRequested: (isRequested: boolean) => void;
  openMembershipPaywall: () => void;
};

const CleaningBasket = ({
  address,
  addressDetails,
  appointment,
  appointmentDate,
  canSubscribeToMembership,
  children,
  cleaningPrestation,
  cleaningPrestationOptions,
  duration,
  hasFreeParking,
  hasMembership,
  hasPets,
  mode,
  openMembershipPaywall,
  pathname,
  proChoices,
  promotion,
  setAddressDetails,
  setIsMembershipRequested,
  shouldShowMembershipCard,
  shouldShowPromoCode,
  shouldUpdateAllOccurrences,
  toggleHasFreeParking,
  universeReference,
  user,
  weeklyRecurrence,
  weeklyRecurrences,
}: CleaningBasketProps) => {
  const { isTaxInfoDisplayed } = CONFIG_BY_COUNTRY[getCurrentCountry()];
  const { prices, pricesError } = useGetPrices();
  const isMultiple = !!cleaningPrestation?.is_recurrent && (mode === 'creation' || shouldUpdateAllOccurrences);
  const userPrices = hasMembership ? prices?.member : prices?.standard;
  const { shouldShowTaxCreditPriceInfo } = useIsITCSelectedOrSuccessfullyPaired({ user, isCesuEnabled: true });

  const prestationPricesCommonProps = {
    cleaningPrestation,
    cleaningPrestationOptions,
    duration,
    hasPets,
    isLoadingPrices: !prices || !!pricesError,
    mode,
    prices: userPrices,
    promotion,
    shouldShowPromoCode,
    universeReference,
    user,
  };

  return validateAddress(address) ? (
    <StyledBasketContainer>
      {!!prices && <span data-testid={BASKET_CLEANING_PRICES_LOADED_TEST_ID} />}
      <Address
        address={address}
        addressDetails={addressDetails}
        setAddressDetails={setAddressDetails}
        hasFreeParking={hasFreeParking}
        toggleHasFreeParking={toggleHasFreeParking}
        hasTopSeparator={false}
      />
      {isMultiple ? (
        <React.Fragment key='multiple'>
          <MultiplePrestationsDetails
            {...prestationPricesCommonProps}
            weeklyRecurrence={weeklyRecurrence}
            weeklyRecurrences={weeklyRecurrences}
          />
          <MultipleCleaningAppointments
            appointmentDate={appointmentDate}
            appointment={appointment}
            weeklyRecurrence={weeklyRecurrence}
          />
        </React.Fragment>
      ) : (
        <React.Fragment key='single'>
          <SinglePrestationsDetails {...prestationPricesCommonProps} />
          <SingleCleaningAppointments appointmentDate={appointmentDate} appointment={appointment} />
        </React.Fragment>
      )}
      {mode === 'creation' && (
        <ProSection proChoices={proChoices} universeRef={universeReference} shouldHideEligiblePros={true} />
      )}
      {shouldShowMembershipCard && (
        <>
          <MembershipCardSeparator />
          <MembershipCard
            isMembershipRequested={hasMembership}
            setIsMembershipRequested={setIsMembershipRequested}
            openMembershipPaywall={openMembershipPaywall}
            pathname={pathname}
            universeReference={universeReference}
            canSubscribeToMembership={canSubscribeToMembership}
            duration={duration}
            cleaningPrestation={cleaningPrestation}
            user={user}
          />
        </>
      )}
      {/* TaxCreditInfoAlert is only displayed in DE to inform the user that tax credit exists but nothing more */}
      {isTaxInfoDisplayed && shouldShowTaxCreditPriceInfo && <TaxCreditInfoAlert />}
      {children}
    </StyledBasketContainer>
  ) : (
    <div data-testid={BASKET_CLEANING_PRICES_LOADED_TEST_ID}>
      <NoContentYet user={user} universe={universeReference} />
    </div>
  );
};

export default memo(CleaningBasket);
